import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getCaptchaImage } from '@/api/auth'
import type { ApiResponse } from '@/types'

/**
 * 图形验证码 composable
 * 用于发送短信验证码前的图形验证码校验
 *
 * 注意：不再自动在 onMounted 中请求验证码。
 * 使用方应在合适的时机（如 onMounted）主动调用 refreshCaptcha()，
 * 避免 dialog 动画期间并发请求被取消。
 */
export function useCaptcha() {
  const captchaLoading = ref(false)
  const captchaImage = ref('')
  const captchaKey = ref('')
  const captchaCode = ref('')

  const refreshCaptcha = async () => {
    if (captchaLoading.value) return
    captchaLoading.value = true
    try {
      const res = await fetchCaptchaWithRetry()
      captchaImage.value = res.data.captchaImage
      captchaKey.value = res.data.captchaKey
      captchaCode.value = ''
    } catch {
      ElMessage.error('获取验证码失败')
    } finally {
      captchaLoading.value = false
    }
  }

  /**
   * 带重试的验证码获取，首次失败后延迟 300ms 重试一次
   * 解决 dialog 动画/Vite 代理首次连接导致的请求取消问题
   */
  const fetchCaptchaWithRetry = async (): Promise<
    ApiResponse<{ captchaKey: string; captchaImage: string }>
  > => {
    try {
      return await getCaptchaImage()
    } catch (error) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return getCaptchaImage()
    }
  }

  return {
    captchaLoading,
    captchaImage,
    captchaKey,
    captchaCode,
    refreshCaptcha,
  }
}
